import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@context/auth";
import { ListItemAction } from "@components/ListItemCardCst/types";
import {
  CARD_ACTION_ICON_MAP,
  CardActionConfig,
  cardActionsRegistry,
  evaluateDisableIf,
  evaluateHideIf,
} from "@utils/cardActions";
import { UseCardActionsOptions, UseCardActionsResult } from "./types";

export type { UseCardActionsOptions, UseCardActionsResult } from "./types";

const userHasPermission = (
  permission: string | undefined,
  userPermissions: string[] | undefined,
): boolean => {
  if (!permission) return true;
  if (!userPermissions || userPermissions.length === 0) return false;
  if (permission.includes("&&")) {
    return permission.split("&&").every((p) => userPermissions.includes(p));
  }
  return userPermissions.includes(permission);
};

export const useCardActions = (
  entityType: string,
  entity: Record<string, any> | undefined,
  options: UseCardActionsOptions = {},
): UseCardActionsResult => {
  const { t } = useTranslation("list-item-card");
  const { user } = useAuth();

  return useMemo(() => {
    if (!entity) return [];
    const allConfigs = cardActionsRegistry[entityType];
    if (!allConfigs || allConfigs.length === 0) return [];

    const configs = allConfigs.filter(
      (config: CardActionConfig) =>
        !evaluateHideIf(config.hideIf, entity, {
          corporateId: options.corporateId,
          user,
        }),
    );
    if (configs.length === 0) return [];

    const noop = () => {
      /* TODO: modificare con funzione reale tramite overrides */
    };

    const built: ListItemAction[] = configs.map((config: CardActionConfig) => {
      const lacksPermission = !userHasPermission(
        config.permission,
        user?.permissions,
      );
      const disabledByCondition = evaluateDisableIf(config.disableIf, entity, {
        corporateId: options.corporateId,
        user,
      });
      const disabled = lacksPermission || disabledByCondition;

      const onClick = options.overrides?.[config.key] ?? noop;

      const labelKey =
        typeof config.labelKey === "function"
          ? config.labelKey(entity)
          : config.labelKey;
      const iconKey =
        typeof config.iconKey === "function"
          ? config.iconKey(entity)
          : config.iconKey;

      return {
        label: t(labelKey, config.key),
        icon: CARD_ACTION_ICON_MAP[iconKey],
        onClick,
        disabled,
      };
    });

    const groupsMap = new Map<number, ListItemAction[]>();
    configs.forEach((config, index) => {
      const list = groupsMap.get(config.group) ?? [];
      list.push(built[index]);
      groupsMap.set(config.group, list);
    });

    return Array.from(groupsMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([, list]) => list);
  }, [entityType, entity, options, t, user]);
};
